import React, { useState, useMemo } from "react";
import { MessageCircle, Eye, ChevronRight, Plus, ThumbsUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { Card, Chip, Button, PageHeader, ReputationBadge } from "../components/Primitives";
import { communityContent } from "../data/mockData";

const TABS = ["questions", "tutorials", "experiences", "events"];

export default function Community() {
  const { t, lang } = useI18n();
  const [tab, setTab] = useState("questions");
  const items = communityContent[tab] || [];

  // Reputation-weighted top answers: sort by (authorReputation * 0.7 + votes*3)
  const topAnswers = useMemo(() => {
    const list = [...(communityContent.topAnswers || [])];
    list.sort((a, b) => (b.authorReputation * 0.7 + b.votes * 3) - (a.authorReputation * 0.7 + a.votes * 3));
    return list.slice(0, 4);
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto animate-fade-up">
      <PageHeader
        title={t("community.title")}
        subtitle={t("community.subtitle")}
        testId="community-header"
        right={<Button data-testid="community-ask" onClick={() => toast?.info?.("Community posting coming soon")}><Plus className="w-4 h-4" />{t("community.ask")}</Button>}
      />

      <Card className="p-3 mb-6 bg-electric-500/[0.05] border-electric-500/30" data-testid="community-static-note">
        <p className="text-[11px] text-ink-700">
          Community content shown below is a curated preview. User posts land here once the community backend ships.
        </p>
      </Card>

      {/* TOP ANSWERS — reputation-weighted */}
      <section className="mb-8" data-testid="top-answers-section">
        <div className="mb-3">
          <h2 className="text-base font-semibold flex items-center gap-2 text-ink-900">
            <Sparkles className="w-4 h-4 text-electric-600" />
            {t("community.topAnswered")}
          </h2>
          <p className="text-xs text-ink-600 mt-1">{t("community.topAnsweredSub")}</p>
        </div>
        <Card className="divide-y divide-carbon-600">
          {topAnswers.map((a) => (
            <div key={a.id} className="p-4 hover:bg-carbon-750/50 transition" data-testid={`top-answer-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center shrink-0 w-14 pt-1">
                  <div className="flex items-center gap-1 text-ink-700">
                    <ThumbsUp className="w-3.5 h-3.5" />
                    <span className="font-mono text-sm font-semibold">{a.votes}</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <Chip>{a.tag}</Chip>
                  </div>
                  <h3 className="text-sm font-medium text-ink-900 leading-snug">
                    {lang === "it" ? a.questionTitle : a.questionTitleEn}
                  </h3>
                  <p className="text-xs text-ink-700 mt-1.5 leading-relaxed">
                    {lang === "it" ? a.answerExcerpt : a.answerExcerptEn}
                  </p>
                  <div className="flex items-center gap-2 mt-2.5">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-ink-500">
                      {t("community.by")}
                    </span>
                    <span className="text-xs font-medium text-ink-900">{a.author}</span>
                    <ReputationBadge score={a.authorReputation} />
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-ink-400 shrink-0 self-center" />
              </div>
            </div>
          ))}
        </Card>
      </section>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-carbon-600" data-testid="community-tabs">
        {TABS.map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`community-tab-${k}`}
            className={`px-4 py-2.5 text-sm relative transition ${
              tab === k ? "text-ink-900" : "text-ink-500 hover:text-ink-700"
            }`}
          >
            {t(`community.tabs.${k}`)}
            {tab === k && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-500" />}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {items.map((item) => (
          <Card
            key={item.id}
            className="hover:border-electric-500/40 transition cursor-pointer p-5"
            data-testid={`community-item-${item.id}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-carbon-750 border border-carbon-600 flex items-center justify-center font-mono text-electric-600 text-xs">
                {item.tag.slice(0, 3).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <Chip className="mb-2">{item.tag}</Chip>
                <h3 className="text-sm font-medium text-ink-900 leading-snug">
                  {lang === "it" ? item.title : item.titleEn}
                </h3>
                <div className="flex items-center gap-4 mt-2 text-[11px] text-ink-500">
                  <span>{t("common.by")} {item.author}</span>
                  {item.answers > 0 && (
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-3 h-3" /> {item.answers} {t("community.answers")}
                    </span>
                  )}
                  {item.views > 0 && (
                    <span className="flex items-center gap-1 font-mono">
                      <Eye className="w-3 h-3" /> {item.views}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-ink-400" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
